import React from "react";
import { BaseModel, BaseSingleModel, BaseUnlistableModel } from "./utils";
import { BaseURLScheme, URLScheme, SingleURLScheme, UnlistableURLScheme } from "./urls";
import {
	REQUEST_CREATE,
	REQUEST_RETRIEVE,
	REQUEST_LIST,
	REQUEST_UPDATE_ONE,
	REQUEST_MANY,
	REQUEST_DELETE_ONE,
	SET_CURRENT_ID,
	SET_CURRENT,
	RESET,
	SET_SEARCH,
	CLEAR_SEARCH,
	SET_QUERY,
	CLEAR_QUERY,
	NEXT_PAGE,
	REQUEST_TOTAL_COUNT,
} from './types';
import invariant from "invariant";
import { useDispatch, useSelector, shallowEqual } from "react-redux";

// Generate hooks from methods
function _wrapDispatch(fn) {
	const dispatch = useDispatch();
	return React.useCallback((...args) => dispatch(fn(...args)), [fn, dispatch]);
}

/*
 * APIEndpoint
 * A set of actions and states for an API.
 */
export default class APIEndpoint {

	/*
	 * APIEndpoint(url)
	 * APIEndpoint(url, options)
	 * APIEndpoint(url, Model)
	 * APIEndpoint(url, Model, options)
	 */
	constructor(urlScheme, Model, options) {
		if (typeof urlScheme === 'string') {
			urlScheme = URLScheme.fromString(urlScheme);
		} else if (urlScheme instanceof Function) {
			urlScheme = new URLScheme(urlScheme);
		}

		invariant(urlScheme instanceof BaseURLScheme, `${urlScheme} is not convertible to an URLScheme`);
		this._urlScheme = urlScheme;
		// called with new APIEndpoint('/path/', { options })
		// Swap Model with options and the default model will be selected
		if (typeof Model !== "function" && options === undefined) {
			options = Model;
			Model = null;
		}
		options = options || {};
		// Name the APIEndpoint from the model, unless it is the default model
		const name = options.name || (Model === null ? "_" : Model.name);
		// If the Model is not specified, guess the default model from the URL Scheme
		this._Model = Model || 
		(	
			this._urlScheme instanceof SingleURLScheme 
				? BaseSingleModel 
				: this._urlScheme instanceof UnlistableURLScheme 
					? BaseUnlistableModel 
					: BaseModel
		);
		// Create the index used to disambiguate between various endpoints
		this.index = options.index || Symbol(`${name}.INDEX`);
		// Apply options
		this.defaultLimit = options.limit || 10;
		this.authorization = options.authorization;

		this.fetchOptions = options.fetchOptions || {};
	}

	clone(options) {
		return new APIEndpoint(this._urlScheme, this._Model, {
			name: `Clone${this._Model.name}`,
			limit: this.defaultLimit,
			authorization: this.authorization,
			fetchOptions: this.fetchOptions,
			...options,
		});
	}

	/**
	 * Merge the headers of the dispatch with the headers of the APIEndpoint
	 */
	_mergeHeaders(headers) {
		if (!this.authorization) {
			return headers;
		}
		return {
			...headers,
			Authorization: this.authorization,
		};
	}

	/** Building query params here for the list request:
         *  type can be any filter we want: 'ordering' 'created', 'last_modified' 
         *  the request will then look like: 
         *  '/?limit=10&offset=0&search=<searchQuery>&ordering=<filter>'
    */

	_buildQueryParams(state, options) {
		const offset = state.offset || 0;
		const limit = options.limit !== undefined ? options.limit : this.defaultLimit;
		const {search, type, filter} = state.query || {};
		const baseQuery = {limit, offset, search: state.search ?? search, ...options.parameters };
		const filteredQuery = type && filter && { [type]: filter};
		return {...baseQuery, ...filteredQuery};
	}

	_getListURL(state, options) {
		if (state.nextURL !== undefined) {
			return state.nextURL;
		}

		if(state.newValues && !state.newValues.length){
			state.offset = 0;
		}

		const query = this._buildQueryParams(state, options);  
		return this._urlScheme.getList(options, query);
	}

	/**
	 * Set the instance pointed by the ID to be the current instance
	 * @param  {number} id - the id
	 * @param  {Object} options - additional options for the URL generation
	 * @return {Object} - The dispatched action
	 */
	setCurrent(id, options={}) {
		id = id && String(id);
		return (dispatch, getState) => {
			const state = this._getSubState(getState());
			invariant(options.update === undefined || typeof options.update === 'boolean',
				`options.update should be a boolean but is ${options.update}`);

			const recycle = !options.update;
			if (state.currentID === id && recycle) {
				return;
			}
			dispatch({
				type: SET_CURRENT_ID,
				index: this.index,
				payload: {
					id,
				},
			});
			if (id === null) {
				return;
			}
			if (recycle) {
				const index = state.index || {};
				const instance = index[id];
				if (instance) {
					dispatch({
						type: SET_CURRENT,
						index: this.index,
						payload: {
							instance,
						},
					});
					return;
				}
			}
			return dispatch(this.retrieve(id, options));
		};
	}

	retrieve(id, options = {}) {
		return {
			type: REQUEST_RETRIEVE,
			index: this.index,
			payload: {
				Model: options.Model || this._Model,
				id,
			},
			request: {
				url: this._urlScheme.getDetail(id, options),
				fetchOptions: this.fetchOptions
			}
		};
	}

	/**
	 * Retrieve the resource from array of ids
	 * @param  {Array} [ids] - List of ids to find
	 * @param  {Object} [options={}] - Options to get resource
	 * @param  {Boolean} options.update - Get all resource from server instead of splitting with index
	 * @return {Object} - The dispatched action
	 */
	retrieveMany(ids, options = {}) {
		return (dispatch, getState) => {
			if (!ids.length) {
				return;
			}

			// deduplicate identifiers
			invariant(ids.every(id => typeof id === 'string' || typeof id === 'number'),
				`incorrect id ${ids}`);
			ids = ids.map(String);
			ids = Array.from(new Set(ids));

			const recycle = !options.update;
			const state = this._getSubState(getState());
			const stateIndex = state.index || {};

			const listToRetrieve = recycle ? ids.filter(id => !stateIndex[id]) : ids;

			if (!listToRetrieve.length) {
				return;
			}

			dispatch({
				type: REQUEST_MANY,
				index: this.index,
				payload: {
					Model: options.Model || this._Model,
					ids: listToRetrieve,
				},
				request: {
					url: this._urlScheme.getList(options, {
						...options.parameters,
						id: listToRetrieve
					}),
					fetchOptions: this.fetchOptions
				},
			});
		};
	}

	/**
	 * Get by parameters
	 * @param  {Object} options - the options of the list
	 * @return {Object} - The action dispatched
	 */
	list(options = {}) {
		return (dispatch, getState) => {
			const state = this._getSubState(getState());
			dispatch({
				type: REQUEST_LIST,
				index: this.index,
				payload: {
					Model: options.Model || this._Model,
					append: options.append,
				},
				request: {
					url: this._getListURL(state, options),
					headers: this._mergeHeaders(options.headers),
					fetchOptions: this.fetchOptions
				}
			});
		};
	}

	/**
	 * Get the total count
	 * @param  {Object} options - the options of the list
	 * @return {Object} - The action dispatched
	 */
	count(options = {}) {
		return (dispatch, getState) => {
			const state = this._getSubState(getState());
			dispatch({
				type: REQUEST_TOTAL_COUNT,
				index: this.index,
				payload: {
					Model: options.Model || this._Model,
				},
				request: {
					url: this._getListURL(state, options),
					headers: this._mergeHeaders(options.headers),
					fetchOptions: this.fetchOptions
				}
			});
		};
	}


	fetchNextPage = (options) => {
		return (dispatch, getState) => {
			const state = this._getSubState(getState());
			dispatch({
				type: NEXT_PAGE,
				index: this.index,
				payload: {
					next: state.next,
					limit: this.defaultLimit,
				},
			});
			return dispatch(this.list({
				...options,
				append: true
			}));
		};
	}

	updateOne = (id, data, options = {}) => {
		return {
			type: REQUEST_UPDATE_ONE,
			index: this.index,
			payload: {
				id: id && String(id),
				Model: options.Model || this._Model,
			},
			request: {
				url: this._urlScheme.getDetail(id, options),
				method: options.partial ? 'PATCH' : 'PUT',
				headers: this._mergeHeaders(options.headers),
				data: data,
				fetchOptions: this.fetchOptions
			},
		};
	}

	/** Set the search on the endpoint */
	setSearch(search, options, dispatchActionType = 'list') {
		return (dispatch, getState) => {
			const { search: currentSearch } = this._getSubState(getState());
			// If the currentSearch is the same as the next, stop
			if (currentSearch === search) {
				return;
			}
			if (!search) {
				if (!currentSearch) {
					// both are empty, it is the same, stop
					return;
				}
				// Clear the search if the searched pattern is empty
				dispatch({
					type: CLEAR_SEARCH,
					index: this.index,
				});
			} else {
				dispatch({
					type: SET_SEARCH,
					index: this.index,
					payload: {
						search,
					},
				});
			}
			if(dispatchActionType === 'count'){
				return dispatch(this.count(options));
			}
			dispatch(this.list(options));
		};
	}

	setQuery(query, options) {
		return (dispatch) => {
			if (!query) {
				dispatch({
					type: CLEAR_QUERY,
					index: this.index,
				});
			} else {
				dispatch({
					type: SET_QUERY,
					index: this.index,
					payload: {
						query,
					},
				});
				query.type === 'ordering' && dispatch(this.list(options));
			}
		};
	}

	setFilters(query, options) {
		return (dispatch) => {
			if (!query) {
				dispatch({
					type: CLEAR_QUERY,
					index: this.index,
				});
			} else {
				dispatch({
					type: SET_QUERY,
					index: this.index,
					payload: {
						query,
					},
				});
				dispatch(this.list(options));
			}
		};
	}

	create = (data, options = {}) => {
		return {
			type: REQUEST_CREATE,
			index: this.index,
			request: {
				method: 'POST',
				url: this._urlScheme.getList(options),
				headers: this._mergeHeaders(options.headers),
				data: data,
				expected: options.conflicts ? [200, 201, 409] : undefined,
				fetchOptions: this.fetchOptions
			},
			payload: {
				Model: options.Model || this._Model,
			},
		};
	}

	delete = (id, options = {}, data=undefined) => {
		return {
			type: REQUEST_DELETE_ONE,
			index: this.index,
			payload: {
				id: String(id),
				Model: data ? this._Model : undefined,
			},
			request: {
				method: 'DELETE',
				url: this._urlScheme.getDetail(id, options),
				headers: this._mergeHeaders(options.headers),
				data: data,
				fetchOptions: this.fetchOptions
			}
		};
	}

	/**
	 * Reset the values contain in the user's state
	 * @return {Object} - Action to reset the users
	 */
	reset() {
		return {
			type: RESET,
			index: this.index,
		};
	}


	_getSubState(state) {
		return state[this.index] || {};
	}

	getTotalFromState = (state) => {
		return this._getSubState(state).total || 0;
	}

	getValuesFromState = (state) => {
		return this._getSubState(state).values || [];
	}

	getSuccessFromState = (state) => {
		return this._getSubState(state).success;
	}

	getNewValuesFromState = (state) => {
		return this._getSubState(state).newValues || [];
	}

	getCurrentFromState = (state) =>  {
		const { current, currentID } = this._getSubState(state);
		return (currentID !== null && current) || null;
	}

	getCurrentOrConflictFromState = (state) => {
		return this._getSubState(state).current || null;
	}

	getCurrentIDFromState(state) {
		return this._getSubState(state).currentID || null;
	}

	getCurrentLoadingFromState(state) {
		return this._getSubState(state).currentLoading || false;
	}

	getEmptyFromState(state) {
		return this._getSubState(state).empty || false;
	}

	getListErrorFromState(state) {
		const error = this._getSubState(state).error; 
		return error !== 200 ? error : null;
	}

	getCreatingFromState = (state) => this._getSubState(state).creating || false;

	getLoadingFromState = (state) => this._getSubState(state).loading || false;

	getUpdatingFromState = (state) => this._getSubState(state).updating || false;

	getSearchFromState(state) {
		return this._getSubState(state).search || null;
	}

	getHasNextFromState = (state) => Boolean(this._getSubState(state).next);

	getLoadingManyFromState = (state) => this._getSubState(state).loadingMany || false;

	getDeletingFromState = (state) => this._getSubState(state).deleting || false;

	getInstanceFromState = (state, id) => {
		invariant((id === null || typeof id === 'string'), `ID must be a string or null but is ${id}`);
		const currentIndex = this._getSubState(state).index || {};
		return (id !== null && currentIndex[id]) || null;
	}

	getManyFromState(state, ids, { ignoreMissing = true } = {}) {
		invariant(ids instanceof Array, 'ids must be a list of id');
		const currentIndex = this._getSubState(state).index || {};
		const instances = ids.map(id => currentIndex[id] || null);
		return ignoreMissing ? instances.filter(Boolean) : instances;
	}

	useCurrentOrConflictFromState = () => useSelector(this.getCurrentOrConflictFromState);

	useCurrent = () => useSelector(this.getCurrentFromState);

	useValues = () => useSelector(this.getValuesFromState);

	useCreating = () => useSelector(this.getCreatingFromState);

	useUpdating = () => useSelector(this.getUpdatingFromState);

	useDeleting = () => useSelector(this.getDeletingFromState);

	useMany = (ids, opts) => useSelector(state => this.getManyFromState(state, ids, opts), shallowEqual);

	// Get an instance from the state by its ID
	useInstanceByID = (id) => useSelector(state => this.getInstanceFromState(state, id));

	useCreate = () => _wrapDispatch(this.create);

	useUpdate = () => _wrapDispatch(this.updateOne);

	useDelete = () => _wrapDispatch(this.delete);

	useFetchNextPage = () => _wrapDispatch(this.fetchNextPage);

	useLoading = () => useSelector(this.getLoadingFromState);
}
